// Apollo

import { ApolloClient } from 'apollo-boost';
import { createHttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { withClientState } from 'apollo-link-state';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import history from 'utils/history';
import store from 'redux/store';
import * as Sentry from '@sentry/react';

// Constants
import constants from "config/constants";
const { API_URI, AUTH_TOKEN } = constants;

class GrapQLError extends Error {
  constructor(message, operationName) {
    super(message);
    this.name = `[GraphQLError] ${operationName}`;
  }
}

const httpLink = createHttpLink({
  // uri: `https://api.c1do1.advancedlapp.com/graphql`,
  uri: `${API_URI}/graphql`,
  //credentials: 'include'
});

const authLink = setContext((_, { headers }) => {

  //const token = Cookies.get(AUTH_TOKEN);
  const token = localStorage.getItem(AUTH_TOKEN);

  // get the authentication token from local storage if it exists
  let locale = localStorage.getItem("locale") || "es_ES";
  locale = locale === "es" ? "es_ES" : locale;
  locale = locale === "en" ? "en_US" : locale;
  locale = locale === "tr" ? "tr_TR" : locale;
  locale = locale === "pt" ? "pt_PT" : locale;

  // return the headers to the context so httpLink can read them
  return { headers: { authorization: token, locale: locale } };
});

const cache = new InMemoryCache({});

const defaultState = {
  searchText: "",
  categoryFilter: null,
};


const errorLink = onError(({ operation, networkError, graphQLErrors, forward }) => {
  if (!networkError && !graphQLErrors) return;

  let authNeeded = false;
  const query = {
    operationName: operation.operationName,
    variables: operation.variables
  };

  if (graphQLErrors) {
    graphQLErrors.forEach(({ code, message }) => {
      // Log detallado para debugging (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[GraphQL Error] ${query.operationName}:`, {
          code,
          message,
          variables: query.variables
        });
      }
      
      Sentry.captureException(
        new GrapQLError(message, query.operationName),
        {
          contexts: {
            query: { ...query, code }
          }
        });
      if (code === 'AUTHENTICATION_REQUIRED')
        authNeeded = true;
    });
  }
  
  if (networkError) {
    // Extraer información del error de red
    const statusCode = networkError.statusCode || (networkError.response && networkError.response.status);
    const errorMessage = networkError.message || 'Network error';
    
    // Log detallado para debugging (solo en desarrollo)
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Network Error] ${query.operationName}:`, {
        statusCode,
        message: errorMessage,
        variables: query.variables,
        fullError: networkError
      });
    }
    
    // Enviar a Sentry con información adicional
    Sentry.captureException(networkError, {
      contexts: {
        query: {
          ...query,
          statusCode,
          errorMessage
        }
      },
      tags: {
        errorType: 'network',
        statusCode: statusCode?.toString() || 'unknown'
      }
    });
    
    // Para errores 500, 502, 503, 504 (errores del servidor), no hacer nada adicional
    // Solo registrar en Sentry. Los componentes deben manejar estos errores con error boundaries
    if (statusCode && [500, 502, 503, 504].includes(statusCode)) {
      // No hacer nada adicional, solo registrar
      return;
    }
  }

  if (authNeeded) {
    // Clear session data to prevent infinite redirect loops
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem('user_id');
    localStorage.removeItem('logged_date');
    localStorage.removeItem('course_id');

    const ltiPlatform = store.getState()['general'].ltiPlatform;
    let pathname = '/auth/login';
    if (ltiPlatform)
      pathname = '/courses/error';

    history.push({
      pathname,
      search: 'expiredSession'
    });
    history.go();
  }
});

const stateLink = withClientState({
  cache,
  defaults: defaultState,
  resolvers: {
    Mutation: {
      updateCategoryFilter: (_, { newCategoryFilter }, { cache }) => {
        cache.writeData({ data: { categoryFilter: newCategoryFilter } });
      },
      updateSearchText: (_, { newSearchText }, { cache }) => {
        cache.writeData({ data: { searchText: newSearchText } });
      },
    },
  },
});

const client = new ApolloClient({
  link: ApolloLink.from([stateLink, errorLink, authLink.concat(httpLink)]),
  cache,
});

export default client;
